import { useState, useEffect } from 'react';
import { getEmailAnalytics } from '../../services/emailAnalyticsService';
import BarChart from '../charts/BarChart';
import PieChart from '../charts/PieChart';

const EmailAnalyticsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [emailData, setEmailData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getEmailAnalytics(30);
        setEmailData(data);
      } catch (error) {
        console.error('Error loading email analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading email analytics...</p>
      </div>
    );
  }

  if (!emailData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No email data available</p>
      </div>
    );
  }

  // Email delivery rates
  const deliveryData = {
    labels: emailData.emailMetrics.map((e: any) => e.emailType),
    datasets: [
      {
        label: 'Sent',
        data: emailData.emailMetrics.map((e: any) => e.sent),
        backgroundColor: '#2563eb',
      },
      {
        label: 'Delivered',
        data: emailData.emailMetrics.map((e: any) => e.delivered),
        backgroundColor: '#10b981',
      },
    ],
  };

  // Campaign effectiveness
  const campaignData = {
    labels: emailData.campaignEffectiveness.map((c: any) => c.campaignType),
    datasets: [
      {
        label: 'Conversion Rate (%)',
        data: emailData.campaignEffectiveness.map((c: any) => c.conversionRate),
        backgroundColor: '#f59e0b',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Email & Communication Analytics
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Sent</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {emailData.totalSent}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Delivery Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {emailData.deliveryRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Open Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {emailData.openRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Click Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {emailData.clickRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Bounce Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {emailData.bounceRate.toFixed(1)}%
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Email Delivery by Type</h3>
          <BarChart data={deliveryData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Campaign Conversion Rates</h3>
          <BarChart data={campaignData} height={300} />
        </div>
      </div>

      {/* Email Metrics Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Email Metrics by Type
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Email Type</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Sent</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Delivered</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Opened</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Clicked</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Bounced</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Delivery Rate</th>
            </tr>
          </thead>
          <tbody>
            {emailData.emailMetrics.map((metric: any, index: number) => {
              const deliveryRate = metric.sent > 0 ? (metric.delivered / metric.sent) * 100 : 0;
              return (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem' }}>{metric.emailType}</td>
                  <td style={{ padding: '1rem' }}>{metric.sent}</td>
                  <td style={{ padding: '1rem' }}>{metric.delivered}</td>
                  <td style={{ padding: '1rem' }}>{metric.opened}</td>
                  <td style={{ padding: '1rem' }}>{metric.clicked}</td>
                  <td style={{ padding: '1rem' }}>{metric.bounced}</td>
                  <td style={{ padding: '1rem', color: deliveryRate > 95 ? '#10b981' : '#ef4444' }}>
                    {deliveryRate.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmailAnalyticsTab;

